import { useFindMany } from "@gadgetinc/react";
import { useContext, useEffect, useMemo, useState } from "react";
import { faUsers } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { api } from "../api";
import { CustomerContext } from "../providers";
import LoadingSpinner from "./LoadingSpinner";
import {
  Sidebar,
  SidebarBody,
  SidebarItem,
  SidebarSection,
  SidebarTitle,
} from "./tailwind/sidebar";

const SEGMENTS_PAGE_SIZE = 250;

const segmentSelect = {
  id: true,
  name: true,
  lastContactsCount: true,
  lastRefreshedAt: true,
  type: true,
  source: true,
  filters: true,
};

const SegmentsSidebar = ({ selectedSegment, onSegmentSelect, refreshToken }) => {
  const { customer } = useContext(CustomerContext);

  const segmentsFilter = {
    customerId: { equals: customer.id },
    isArchived: { notEquals: true },
  };

  const [{ data: segments, fetching }, refetchSegments] = useFindMany(
    api.segment,
    {
      live: true,
      first: SEGMENTS_PAGE_SIZE,
      filter: segmentsFilter,
      select: segmentSelect,
      sort: { name: "Ascending" },
    },
  );

  useEffect(() => {
    if (refreshToken !== undefined) {
      refetchSegments();
    }
  }, [refreshToken]);

  const [additionalSegments, setAdditionalSegments] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    if (!segments?.hasNextPage) {
      setAdditionalSegments([]);
      return;
    }

    let cancelled = false;
    setLoadingMore(true);

    (async () => {
      const collected = [];

      let page = await api.segment.findMany({
        first: SEGMENTS_PAGE_SIZE,
        after: segments.endCursor,
        filter: segmentsFilter,
        select: segmentSelect,
        sort: { name: "Ascending" },
      });
      collected.push(...page);

      while (page.hasNextPage && !cancelled) {
        page = await page.nextPage();
        collected.push(...page);
      }

      if (!cancelled) {
        setAdditionalSegments(collected);
        setLoadingMore(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [segments, customer.id]);

  // Create the segments list with "All contacts" as the first option
  const segmentsList = useMemo(() => {
    const allContactsOption = {
      id: "all-contacts",
      name: "All contacts",
      lastContactsCount: null,
      lastRefreshedAt: null,
      type: "default",
      source: "simio",
      filters: [],
    };

    return segments
      ? [allContactsOption, ...segments, ...additionalSegments]
      : [allContactsOption];
  }, [segments, additionalSegments]);

  const handleSegmentClick = (segment) => {
    onSegmentSelect(segment);
  };

  const formatContactCount = (count) => {
    if (count === null || count === undefined) return "";
    return count.toLocaleString();
  };

  return (
    <Sidebar className="w-60 border-r m-0 p-0 border-box h-full">
      <SidebarBody>
        <SidebarSection>
          <SidebarTitle>
            <FontAwesomeIcon icon={faUsers} className="text-base" />
            Segments
          </SidebarTitle>
          {fetching || loadingMore ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : (
            segmentsList.map((segment) => (
              <SidebarItem
                key={segment.id}
                current={selectedSegment?.id === segment.id}
                onClick={() => handleSegmentClick(segment)}
                className="cursor-pointer ml-2 hover:bg-grey rounded-customBorderRadius my-0 leading-3"
              >
                <div className="flex items-baseline justify-between w-full">
                  <span className="text-sm font-medium text-zinc-950 truncate">
                    {segment.name}
                  </span>
                  {segment.type !== "default" &&
                    segment.lastContactsCount !== null && (
                      <span className="text-xs text-zinc-600 ml-2">
                        {formatContactCount(segment.lastContactsCount)}
                      </span>
                    )}
                </div>
              </SidebarItem>
            ))
          )}
        </SidebarSection>
      </SidebarBody>
    </Sidebar>
  );
};

export default SegmentsSidebar;
